import React from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { gaEvent } from "../../analytics";
import { CONTACT_WHATSAPP, ORDER_TEXTS } from "../shared/constants";

const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(2),
}));

const WhatsAppButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.brand.whatsapp,
  color: theme.palette.common.white,
  textTransform: "none",
  fontWeight: 700,
  borderRadius: 999,
  padding: theme.spacing(1.25, 3),
  "&:hover": { backgroundColor: theme.palette.brand.whatsappHover },
}));

/**
 * The step the checkout used to take on the shopper's behalf.
 *
 * `ProcessOrderPage` assigned `window.location.href` the instant the order posted, so the sale
 * closed without anyone ever seeing a total, a delivery date or an order id. This is that same
 * handoff, one tap later and on a page that shows all three (README §7.3).
 *
 * Two details are load-bearing:
 *
 * - **The number is `CONTACT_WHATSAPP`**, which is what the old auto-redirect used — *not* the
 *   `WHATSAPP_PHONE` the footer's generic button uses. They are different numbers, and sending
 *   confirmed orders to the other one would quietly reroute every sale.
 * - **The GA4 event keeps its name and `source`.** `whatsapp_click` with `source:
 *   "order_confirm"` is exactly what the redirect fired, so the before/after rate of this change
 *   is a comparison of one series against itself rather than of two different events.
 */
const WhatsAppHandoff: React.FC<{ orderId: string }> = ({ orderId }) => {
  const handleClick = () => {
    gaEvent("whatsapp_click", { source: "order_confirm", order_id: orderId });

    const message = `Hola orientador biai, ayúdame a procesar el pago del pedido ${window.location.origin}/pedido/${orderId}`;
    const url = `https://api.whatsapp.com/send?phone=${CONTACT_WHATSAPP}&text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <Wrapper>
      <WhatsAppButton
        variant="contained"
        onClick={handleClick}
        endIcon={<WhatsAppIcon />}
        fullWidth
      >
        {ORDER_TEXTS.whatsappButton}
      </WhatsAppButton>
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {ORDER_TEXTS.whatsappHint}
      </Typography>
    </Wrapper>
  );
};

export default WhatsAppHandoff;
